/**
 * FileBufferCache — reads uploaded files into a cached Uint8Array *immediately* on upload,
 * so later processing never depends on the original File object still being accessible.
 *
 * Root cause of FILE_LOST (~100 cases/week in prod telemetry):
 *   User uploads a file. Browser keeps a File reference that's really a pointer into the
 *   disk or network-cached blob. After a while — tab backgrounded, low-memory pressure,
 *   the file location changing (OneDrive/iCloud), or the user moving/renaming the file —
 *   File.arrayBuffer() rejects with NotFoundError. Tools that only grabbed the bytes
 *   later (at export time) lose the user's work.
 *
 * Fix: prime the bytes into RAM the moment the user picks the file. All subsequent
 * operations read from the cache, not the file handle.
 *
 * Usage:
 *   // On file-select, kick off the cache read (non-blocking, returns a Promise).
 *   FileBufferCache.prime(file);   // fire-and-forget, or await for guaranteed availability
 *
 *   // At processing time, get the bytes:
 *   const bytes = await FileBufferCache.get(file);  // Uint8Array
 *
 *   // Or a Blob (useful for decodeAudioData / FFmpeg inputs expecting Blob):
 *   const blob  = await FileBufferCache.asBlob(file, file.type);
 *
 * Memory: this holds the full file bytes. For a 100 MB MP3 that's 100 MB in RAM until
 * the user changes files or we explicitly release. When the user picks a new file (size
 * or lastModified differs), the old buffer is dropped automatically.
 */
(function(){
'use strict';

var _map = new Map();   // key → { bytes: Uint8Array, primedAt: timestamp }
var _order = [];         // true LRU — most-recently-used at END; oldest at FRONT gets evicted
// In-flight reads, key → Promise<Uint8Array>. prime() only ever checked the
// COMPLETED cache, so a tool that asked for the bytes while the uploader's
// pick-time prime was still running started a SECOND full read of the same
// file. Measured on /slideshow-maker with a 7.6 MB photo: 3 whole-file reads
// per picked image. Joining the in-flight promise makes concurrent readers
// share one read — every tool benefits, and picking a dozen photos is whole
// megabytes of duplicated work saved.
var _inflight = new Map();
// Sticky "known-bad" keys — once prime() has seen file.arrayBuffer() throw
// NotReadable / NotFound, the OS file handle is dead and won't recover.
// Retrying arrayBuffer every time a tool reads would pile up N events per
// session (enhance-video saw 10+ cascade hits for a single revoked handle).
// Flagging the key lets readBytes() throw FILE_LOST immediately instead of
// walking into the same exception twice. Cleared on release() / clear().
// Map (not Set): the VALUE is the _classifyReadError() verdict, so the
// fast-fail path below can still say WHY without touching the dead handle.
var _dead = new Map();
// Identity map for anonymous Blob objects (Blobs that aren't File handles,
// so they have no .name / .lastModified). Two distinct Blobs of the same
// byte length must NOT share a cache key — that was the latent bug behind
// the 2026-06-02 acx-mastering "Stage A bytes returned for Stage B" report.
// A WeakMap keyed by the Blob instance lets us hand out a synthetic id per
// Blob identity, so two structurally-distinct Blobs get distinct cache keys
// and the SAME Blob object read twice still hits cache. Entries vanish
// when the Blob is garbage-collected — no manual cleanup needed.
var _blobIds = new WeakMap();
var _blobIdCounter = 0;

// Blob.arrayBuffer() polyfill: iOS 12 / Safari < 14 don't expose it.
// Without this every prime() throws "file.arrayBuffer is not a function"
// (TIMBRICA-FRONTEND-89) and the tool is unusable on a still-shipping iOS
// version. FileReader.readAsArrayBuffer is the same byte source — just
// the older API contract — so we route through it transparently.
function fileToBytes(file, floorMs){
    var size = (file && file.size) || 0;
    if (file && typeof file.arrayBuffer === 'function') {
        var p;
        try { p = file.arrayBuffer(); } catch (e) { return Promise.reject(e); }
        return _withDeadline(p, size, floorMs).then(function(ab){ return new Uint8Array(ab); });
    }
    return _withDeadline(new Promise(function(resolve, reject){
        var reader = new FileReader();
        reader.onload = function(){ resolve(new Uint8Array(reader.result)); };
        reader.onerror = function(){ reject(reader.error || new Error('FileReader failed')); };
        try { reader.readAsArrayBuffer(file); }
        catch (e) { reject(e); }
    }), size, floorMs);
}

// ── Every read has a deadline (2026-10-24) ───────────────────────────────
// Support chat 714 (23.10.2026, /extend-song, Safari 26.6.1 on macOS): the same
// 8 MB mp3 this Safari had read twice at 20:31 could not be read at 20:47, nor
// could a file it had downloaded fifteen minutes earlier. Every pick went the
// same way — two reads rejected NotReadableError, the third NEVER SETTLED. No
// read here had a bound, so the uploader's verdict never came, the tool's decode
// (which joins this read) waited with it, and each re-pick of the same file
// joined the same pending promise: 644 s of «Читаю файл… 5 %», the run ledger
// keeping the screen awake, not one word on the page. In Yandex Browser on the
// same Mac the file read at once. The browser's fault; waiting forever is ours.
//
// The bound is against «never», not against «slow», and follows the file: a
// read may take as long as the whole file would at 512 KB/s (a OneDrive/iCloud
// placeholder hydrating over a slow link before its first byte still fits), and
// never less than the floor — 30 s for the first read, 5 s for a rescue attempt
// after the browser has already refused this file. Counted in ACTIVE time
// (__activeNow): a frozen or sleeping page is not a stalled read. A stalled read
// is not retried: what stuck is the browser's read machinery, and a read of a
// different shape waits on the same machinery. Stand —
// tests/js/filebytes-read-has-a-deadline.test.mjs.
var READ_FLOOR_MS = 30000;
var READ_RETRY_FLOOR_MS = 5000;
var READ_MIN_BYTES_PER_S = 512 * 1024;
function _readDeadlineMs(size, floorMs){
    return Math.max(floorMs || READ_FLOOR_MS, Math.round((size || 0) / READ_MIN_BYTES_PER_S * 1000));
}
function _activeClock(){
    try { if (typeof window !== 'undefined' && typeof window.__activeNow === 'function') return window.__activeNow(); } catch (_) {}
    return Date.now();
}
function _stalledError(ms){
    var text = 'The browser started reading the file and did not finish in ' + Math.round(ms / 1000) + ' s';
    var e;
    try { e = new DOMException(text, 'NotReadableError'); }
    catch (_) { e = new Error(text); e.name = 'NotReadableError'; }
    try { e.readStalled = true; e.stallMs = ms; } catch (_) {}
    return e;
}
function _withDeadline(promise, size, floorMs){
    if (typeof setInterval !== 'function') return Promise.resolve(promise);
    var limit = _readDeadlineMs(size, floorMs);
    return new Promise(function(resolve, reject){
        var t0 = _activeClock(), done = false;
        // Polled, not one setTimeout: the active clock stands still while the
        // page is frozen, and only a poll can notice that.
        var timer = setInterval(function(){
            var el = _activeClock() - t0;
            if (el > limit) { finish(); reject(_stalledError(el)); }
        }, 1000);
        function finish(){ done = true; clearInterval(timer); }
        Promise.resolve(promise).then(
            function(v){ if (done) return; finish(); resolve(v); },
            function(e){ if (done) return; finish(); reject(e); });
    });
}

// ── «Не файл, а браузер» — the one verdict re-picking cannot fix ───────────
// The advice for an unreadable file is about the FILE (download it from the
// cloud; pick it again) — and for chat 714 both were false: the file was local
// and she re-picked it seven times. Two kinds of evidence say the browser, not
// the file, is what failed, and only evidence decides — never the user agent:
//   · a read the browser accepted and never finished (above);
//   · a fresh pick of a file this browser HAS read before, unchanged (same name,
//     size and modification time) — the file did not change, the browser did.
// Remembered per browser for a day as hashes (the key carries the file's name,
// and names do not go into storage), at most 40.
var READ_OK_KEY = 'tmb_fb_read_ok';
var READ_OK_TTL_MS = 24 * 3600 * 1000;
function _keyHash(key){
    var h = 0x811c9dc5;
    for (var i = 0; i < key.length; i++) { h ^= key.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return h.toString(36);
}
function _readOkList(){
    try {
        var list = JSON.parse(localStorage.getItem(READ_OK_KEY) || '[]');
        if (!Array.isArray(list)) return [];
        var now = Date.now();
        return list.filter(function(r){ return r && typeof r.h === 'string' && now - (+r.t || 0) < READ_OK_TTL_MS; });
    } catch (_) { return []; }
}
function _noteReadOk(key){
    if (!key) return;
    try {
        var h = _keyHash(key);
        var list = _readOkList().filter(function(r){ return r.h !== h; });
        list.push({ h: h, t: Date.now() });
        if (list.length > 40) list = list.slice(-40);
        localStorage.setItem(READ_OK_KEY, JSON.stringify(list));
    } catch (_) {}
}
function _wasReadOkBefore(key){
    if (!key) return false;
    var h = _keyHash(key);
    return _readOkList().some(function(r){ return r.h === h; });
}
/* The verdict for a pick-time failure: the file's own kind, or 'browser' when
   the evidence above says the browser failed. A memory wall, a cancel and a
   moved file keep their own kinds — each has its own true sentence. */
function _pickVerdict(err, key){
    var kind = _classifyReadError(err);
    if (kind === 'oom' || kind === 'aborted' || kind === 'not_found') return { kind: kind, why: '' };
    if (err && err.readStalled) return { kind: 'browser', why: 'stall' };
    if (_wasReadOkBefore(key)) return { kind: 'browser', why: 'read_before' };
    return { kind: kind, why: '' };
}

// ── Chunked read (NotReadableError rescue) ───────────────────────────────
// The whole-file `file.arrayBuffer()` in fileToBytes() is one monolithic OS
// read. On large files (50 MB+) Chrome-on-Windows sporadically throws
// `NotReadableError` when that single contiguous read collides with something
// touching the file mid-read — antivirus scanning the freshly-picked file, a
// OneDrive/network path streaming the blob, a spinning disk, or the OS holding
// a brief lock. Retrying the SAME whole-file read (the pre-2026-07-05 prime()
// ladder did this 3×) reproduces the SAME failure — a degenerate retry.
//
// Reading in chunks is qualitatively different: each slice is a smaller,
// independent OS read that can retry on its own, and no single read needs the
// whole file contiguously available at once. This rescues the transient
// large-file NotReadableError class that fleet telemetry shows as the #1 prime
// failure (repair-video 301, join 108, … repair-audio 16 events / 30 d).
// Root case: Preston w1946preston 2026-07-04 /en/repair-audio, 51 MB
// TAPE002.MP3 → 3× whole-file arrayBuffer NotReadableError → FILE_LOST.
function readSliceBytes(blob){
    // A slice is the same read as the whole file, only shorter — same deadline.
    return fileToBytes(blob, READ_RETRY_FLOOR_MS);
}

// Read one slice with up to `retries` extra attempts on transient failure.
// A stalled slice is not retried (see «Every read has a deadline»).
function readSliceWithRetry(blob, retries){
    return readSliceBytes(blob).catch(function(err){
        if (retries <= 0 || (err && err.readStalled)) throw err;
        var backoff = 120 * (3 - retries); // 120 ms, 240 ms
        return new Promise(function(r){ setTimeout(r, backoff); })
            .then(function(){ return readSliceWithRetry(blob, retries - 1); });
    });
}

// OOM / abort signatures where chunking cannot help — a chunked read still
// allocates the full output Uint8Array, so a genuine allocation wall stays a
// wall. Don't burn a chunked attempt on these; let prime() go straight to dead.
function _readErrorIsFatal(err){
    return _isMemoryWall(err) || _isAbort(err);
}
function _isMemoryWall(err){
    var msg = ((err && err.message) || String(err)) + ' ' + ((err && err.name) || '');
    return /out of memory|allocation failed|cannot allocate|array buffer allocation/i.test(msg);
}
function _isAbort(err){
    var msg = ((err && err.message) || String(err)) + ' ' + ((err && err.name) || '');
    return /AbortError|abort(ed)?\b/i.test(msg) && !_isMemoryWall(err);
}

// ── Why the read failed, in the three shapes that want DIFFERENT advice ──────
// Somebody has to tell the user something, and "could not read the file, try
// another one" is the sentence that sent our 2026-08-25 reporter to pick a
// SECOND cloud photo and fail again. The names below are the browser's own
// DOMException names:
//
//   'read_error' - NotReadableError. The path resolves, the bytes do not. On
//                  Android this is overwhelmingly a Google Photos / Drive pick
//                  of a photo that is not ON the phone (crbug.com/41452449,
//                  open since 2019); on desktop it is an antivirus / OneDrive
//                  / network-share lock. Measured 19-25.08 on the RU shard:
//                  1.77% of every Android-Chrome pick vs 0.11% of
//                  Windows-Chrome - 16x, same code, so it is the platform.
//   'not_found'  - NotFoundError. The file MOVED, was renamed or deleted after
//                  the pick. Re-picking genuinely fixes this one.
//   'oom'        - allocation wall; nothing about the file is wrong.
//
// A function rather than a lookup at throw time, so a caller can classify a
// raw browser error it caught itself without going through prime().
function _classifyReadError(err){
    if (_isMemoryWall(err)) return 'oom';
    if (_isAbort(err)) return 'aborted';
    // 'browser' - the browser accepted the read and never finished it: the file
    //             may be fine, the browser is not (chat 714, «Every read has a
    //             deadline»). Re-picking does not help; restarting it does.
    if (err && err.readStalled) return 'browser';
    var name = (err && err.name) || '';
    if (name === 'NotFoundError') return 'not_found';
    if (name === 'NotReadableError' || name === 'SecurityError') return 'read_error';
    var msg = ((err && err.message) || String(err));
    if (/not\s*found|no longer (exists|accessible)/i.test(msg)) return 'not_found';
    return 'read_error';
}

// Chunked whole-file read: slice into `chunkSize` pieces, read each with its
// own retry, concatenate into one Uint8Array. Same peak memory as whole-file
// (the goal here is read RELIABILITY, not memory — WORKERFS streaming handles
// memory at process time), but far more robust against transient OS read
// failures on large files.
function readChunked(file, chunkSize){
    var size = file.size || 0;
    if (!size) return readSliceBytes(file);
    var out = new Uint8Array(size);
    var offset = 0;
    function step(){
        if (offset >= size) return Promise.resolve(out);
        var end = Math.min(offset + chunkSize, size);
        var at = offset;
        return readSliceWithRetry(file.slice(offset, end), 2).then(function(bytes){
            out.set(bytes, at);
            offset = end;
            return step();
        });
    }
    return step();
}

function keyFor(file){
    if (!file) return null;
    // True File handle (user upload): name + size + lastModified is unique
    // enough AND deterministic across re-reads of the same File object. We
    // *want* to cache these — they're the persistent inputs the FILE_LOST
    // retry chain was built around. A re-upload of the literally same file
    // (same name/size/lastModified) deliberately collides → cache hit, no
    // re-read needed.
    if (file instanceof File && (file.name || file.lastModified)) {
        return file.name + '|' + (file.size || 0) + '|' + (file.lastModified || 0);
    }
    // Anonymous Blob — assign a per-Blob synthetic id via the WeakMap so
    // that two structurally-distinct Blob objects of the same byte length
    // never share a cache key. The legacy fallback used `'' || ''` for
    // name/lastModified and hashed every anonymous Blob to `'|<size>|0'` —
    // any two distinct same-sized Blobs collided and the second reader got
    // the first's bytes. Surfaced 2026-06-02 in /acx-mastering (sid
    // b47dadb9): Stage A's 1104044-byte ACX-mode WAV blob was returned by
    // FileBytes when Stage B's distinct 1104044-byte preserve-mode WAV blob
    // was read. The hidden bleed survived the earlier .slice() fix because
    // .slice() was guarding against ArrayBuffer-detach, not against
    // cross-blob identity confusion.
    if (file instanceof Blob) {
        var existing = _blobIds.get(file);
        if (existing) return existing;
        var id = 'blob@' + (++_blobIdCounter) + '|' + (file.size || 0);
        _blobIds.set(file, id);
        return id;
    }
    return null;
}

// Shape a consistent FILE_LOST error so tools' `_isFileLostErr()` /
// `classifyError()` helpers recognize it. DOMException with name
// NotFoundError matches the codepath that enhance-video / video-crop /
// video-trim already handle — user sees "please re-select your file"
// instead of a cryptic NotReadableError mid-process.
// `kind` is the _classifyReadError() verdict of the ORIGINAL browser error and
// rides along as `.fileLostKind`. It has to be carried, not re-derived: the
// wrapper below is deliberately a NotFoundError whatever went wrong (that is
// the name every tool's _isFileLostErr() already matches), so by the time a
// caller sees it the one bit that decides what to TELL the user - "your file
// moved" vs "your file is in the cloud and not on this device" - has been
// flattened away. Losing it is exactly how a NotReadableError on an Android
// cloud photo came out as "try a different file".
/* The error a failed read answers with — by what actually failed (2026-09-27).
   Until then EVERY failure was dressed as FILE_LOST («The selected file is no
   longer accessible. Please re-select your file.», a NotFoundError), the memory
   wall and a cancelled read included, and the file was flagged dead for the rest
   of the page. RU 13–27.09: ~30 sessions a fortnight where the browser could not
   ALLOCATE the bytes were told their file was gone — /repair-video on a desktop
   «телефон отозвал доступ, выберите файл заново» and every strategy aborted
   (1.3 GB), /join «Добавьте файл снова», /3d-audio, /enhance-video on a 2 GB
   phone. Re-picking cannot help any of them.
     'oom'     → a RangeError whose message keeps the allocation phrase, so
                 FFmpegProcessor.classifyError() says 'memory' and the tool's
                 memory text is what the person reads; isFileLostError() is false.
     'aborted' → the caller's own AbortError, untouched.
     otherwise → the FILE_LOST shape every tool already routes to «re-pick».
   Only the last one marks the file dead: the other two say nothing about it. */
function _readFailure(err, kind, file){
    if (kind === 'oom') {
        var mb = Math.round(((file && file.size) || 0) / 1048576);
        var e = new RangeError('Array buffer allocation failed: the file (' + mb + ' MB) does not fit in this page\'s memory' +
            ((err && err.message) ? ' (' + err.message + ')' : ''));
        try { e.fileLostKind = 'oom'; e.fileLostName = (err && err.name) || 'RangeError'; e.memoryWall = true; } catch (_) {}
        return e;
    }
    if (kind === 'aborted' && err && typeof err === 'object') {
        try { err.fileLostKind = 'aborted'; } catch (_) {}
        return err;
    }
    return _fileLostError(err && err.message, kind, (err && err.name) || '');
}

function _fileLostError(origMsg, kind, origName){
    var text = 'FILE_LOST: The selected file is no longer accessible. Please re-select your file.' +
        (origMsg ? ' (' + origMsg + ')' : '');
    var e;
    try {
        e = new DOMException(text, 'NotFoundError');
    } catch (_) {
        e = new Error(text);
        e.name = 'NotFoundError';
    }
    try {
        e.fileLostKind = kind || 'read_error';
        // The ORIGINAL DOMException name, for the same reason `kind` is carried:
        // the wrapper is always a NotFoundError, so anything reading `.name`
        // downstream (telemetry included) would report the wrapper and quietly
        // turn every NotReadableError in the fleet into a NotFoundError.
        if (origName) e.fileLostName = origName;
    } catch (_) {}
    return e;
}

// Bump a key to MRU position on every access, so eviction targets genuinely stale
// entries (not just the first-inserted). Cheap: one indexOf + splice per access, which
// is O(n) over _order.length — bounded by the ~5-10 files a session typically holds.
function touch(key){
    var idx = _order.indexOf(key);
    if (idx !== -1) _order.splice(idx, 1);
    _order.push(key);
}

/* ── Aggregate RAM budget — device-aware, not a flat gigabyte ────────────────
   The per-file cap (_tooBigForRam, 100 MB) is the only gate a single pick used
   to pass through, and it says nothing about how MANY files arrive. A pick of
   94 photos of 7 MB each is 94 individually-small files and 637 MB of RAM;
   measured on /archive-creator 2026-09-09, that copy was still resident 12 s
   after the pick and was ~22% of a 3.0 GB peak on a job whose own worker reads
   every byte lazily from disk.

   The flat 1 GB ceiling below it was device-blind: a 4 GB Windows laptop and a
   32 GB workstation were allowed the same hoard. `_constrainedDevice()` existed
   already but only ever narrowed the VIDEO per-file cap, so a photo/document
   pick escaped it entirely.

   ~6% of the device's RAM keeps every desktop budget where it was (16 GB still
   gets the old gigabyte) and stops the cohort that actually fails. Over budget
   is not "no insurance": tool-uploader spills to the OPFS tier instead, which
   is the same FILE_LOST rescue at zero heap cost. */
function ramBudget(){
    var B = (typeof window !== 'undefined') && window.__memBudget;
    var gb = (B && typeof B.gb === 'function') ? B.gb() : 8;
    var mb = Math.round(gb * 64);
    if (mb < 128) mb = 128;
    if (mb > 1024) mb = 1024;
    return mb * 1024 * 1024;
}

function ramUsed(){
    var total = 0;
    _map.forEach(function(v){ total += v.bytes.length; });
    return total;
}

function evictIfOverBudget(){
    // Oldest-accessed entries evict first. For typical single-file tool flow
    // this never fires.
    var budget = ramBudget();
    var total = ramUsed();
    while (total > budget && _order.length > 1) {
        var oldest = _order.shift();
        var entry = _map.get(oldest);
        if (entry) { total -= entry.bytes.length; _map.delete(oldest); }
    }
}

/* Wait until the document is visible again. Used to retry prime() after a
   failure that happened mid-tab-background — the OS / browser revokes the
   File handle when the tab isn't foreground, but the handle is fresh again
   when the user returns. Without this retry, one bad prime during the first
   second after file pick permanently flags the key as dead, guaranteeing
   FILE_LOST at every later read. enhance-video was the worst sufferer
   because its flow (select → probe → UI → Enhance) averages 8.6 min between
   pick and exec. */
function whenVisible(){
    if (typeof document === 'undefined' || !document.hidden) return Promise.resolve();
    return new Promise(function(resolve){
        function handler(){
            if (!document.hidden) {
                document.removeEventListener('visibilitychange', handler);
                resolve();
            }
        }
        document.addEventListener('visibilitychange', handler);
    });
}

/* Потолок RAM-прайма для ВИДЕО на слабой памяти.
   Аплоадер это правило уже знал (32 МБ на слабом устройстве против 100 МБ иначе,
   выше — на дисковый ярус), но 37 файлов зовут prime()/cacheFile() НАПРЯМУЮ и
   правило обходили: у автора жалобы 01.09.2026 (Redmi Note 9 Pro, 4 ГБ) зона
   аплоадера честно отправила его 70-МБ ролик в спилл, а тул тут же положил те же
   70 МБ в кучу вызовом FFmpegProcessor.cacheFile(). Правило должно жить у
   владельца байтов, иначе каждый следующий вызывающий переоткрывает его заново.
   Читаемость файла при этом ПРОВЕРЯЕТСЯ (64 КБ с начала): именно за ней тулы и
   зовут cacheFile({throwOnFail:true}) — за приговором «файл мёртв», а не за
   байтами. Дисковый дубль (spill) даёт ту же страховку от отзыва хэндла. */
var PRIME_VIDEO_RAM_CAP = 32 * 1024 * 1024;
var PRIME_RAM_CAP = 100 * 1024 * 1024;
function _constrainedDevice(){
    var B = (typeof window !== 'undefined') && window.__memBudget;
    if (B && typeof B.gb === 'function') return B.gb() <= 4;
    return false;
}
/* A phone, whatever its RAM number: the renderer's practical ceiling there is
   a fraction of a desktop's with the same deviceMemory (an 8 GB Android tab
   dies around 1.5 GB; a 4 GB iPhone tab at ~1 GB). userAgentData first, the
   UA string for the browsers that lack it. Test seam: window.__fbcMobile. */
function _mobileDevice(){
    if (typeof window === 'undefined') return false;
    if (typeof window.__fbcMobile === 'boolean') return window.__fbcMobile;
    try { if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') return navigator.userAgentData.mobile; } catch (_) {}
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}
function _tooBigForRam(file){
    var t = String((file && file.type) || '').toLowerCase();
    var n = String((file && file.name) || '').toLowerCase();
    var isVideo = t.indexOf('video/') === 0 || /\.(mp4|mov|m4v|webm|mkv|avi|wmv|flv|3gp|ts|mpg|mpeg)$/.test(n);
    /* Video on a PHONE never enters the RAM tier (23.09.2026). Every consumer
       of a clip reads it by slices (demuxLazy, robustRange windows) or hands
       the File itself to <video> / WORKERFS — the cached bytes are never read
       back — while the cache sat in the one process that cannot afford it:
       measured on the 22.09 ticket's deck, 228–345 MB of clip bytes primed
       into the renderer before «Export» was even pressed (budget dm×64 MB =
       512 MB on an 8 GB Galaxy S23, whose tab died at ~1.4 GB). The OPFS
       tier gives the same FILE_LOST insurance at zero heap. Desktop keeps
       the RAM tier: FFmpeg's MEMFS lanes copy the bytes once per run and the
       cache saves that read. */
    if (isVideo && _mobileDevice()) return true;
    var cap = (isVideo && _constrainedDevice()) ? PRIME_VIDEO_RAM_CAP : PRIME_RAM_CAP;
    return (file && file.size || 0) > cap;
}

/* prime(file) — «make this file durable», fire-and-forget from pick handlers.
   Resolves to the bytes ONLY below the RAM cap; above it the file is spilled to
   disk and the promise resolves to null. ⛔ Never hand that value to a
   consumer: `new Blob([null])` is the text «null» (the 2026-09-03…09 «clip N:
   moov atom not found» class). Bytes → read()/arrayBuffer(); a File for
   WORKERFS/<video>/Worker → asFile()/asBlob()/settle(). */
function prime(file){
    if (!(file instanceof File) && !(file instanceof Blob)) return Promise.resolve(null);
    var key = keyFor(file);
    if (!key) return Promise.resolve(null);
    /* Имя файла человека — в реестр телеметрии (window.__fileless.note, 17.10.2026): сюда
       приходят и файлы, выбранные мимо зоны загрузки (черновик из IndexedDB, File System
       Access), а их имя ехало в аварийный канал ключом этого кэша (filebytes-prime-fail). */
    try { if (file.name && typeof window !== 'undefined' && window.__fileless && window.__fileless.note) window.__fileless.note(file.name); } catch (_) {}

    if (!_map.has(key) && _tooBigForRam(file)) {
        try { if (typeof window !== 'undefined' && window.__ae) window.__ae('feature_use', {
            f: 'filebytes_prime_capped', szMB: Math.round((file.size || 0) / 1048576),
            gb: (window.__memBudget && window.__memBudget.gb) ? window.__memBudget.gb() : 0 }); } catch (_) {}
        try { spill(file); } catch (_) {}
        /* Приговор о нечитаемом файле остаётся: 64 КБ достаточно, чтобы отозванный
           хэндл бросил ту же ошибку, что и полное чтение. */
        return fileToBytes(file.slice(0, 65536), READ_FLOOR_MS).then(
            function(){ _noteReadOk(key); return null; },
            function(err){ var lost = _readFailure(err, _pickVerdict(err, key).kind, file); _announceDeath(key, lost); throw lost; });
    }

    var existing = _map.get(key);
    if (existing && existing.bytes && existing.bytes.length > 0) {
        touch(key);
        return Promise.resolve(existing.bytes);
    }
    // Same file already being read — join that read instead of starting a
    // duplicate one. The retry ladder lives inside doRead, so a joiner gets
    // the rescued bytes too; on failure the entry is cleared below and a later
    // explicit prime() still gets its fresh attempt.
    var pending = _inflight.get(key);
    if (pending) return pending;
    // prime() is an *explicit* "try now" call — the caller (a tool's file-pick
    // handler) is asking us to (re-)read this file. The _dead flag was meant
    // to short-circuit redundant background reads within a single session,
    // not to permanently lock out a file that the user re-selected. Without
    // this clear, a transient revocation during the first prime ms-after-pick
    // (common on mobile / backgrounded tabs) marks the key dead, and every
    // subsequent re-upload of the SAME file (same name|size|lastModified)
    // gets rejected without a fresh File handle ever being touched.
    // Telemetry: enhance-video saw 81 FILE_LOST events with the same file
    // size repeating 8+ times in a session — classic re-pick lockout.
    if (_dead.has(key)) _dead.delete(key);

    // Read the bytes now, while the File handle is still guaranteed live.
    // Retry strategy (Stage 4 etalon, 2026-05-09 — closes file_lost_at_select
    // cluster of 21 sessions/24h):
    //   1) If document.hidden at fail time → wait for visibility, retry once
    //      (existing pattern — handle revocation during app-switch is transient)
    //   2) Otherwise → up to 2 retries with 200 ms / 800 ms backoff. Mobile
    //      browsers sometimes reject the FIRST arrayBuffer() call milliseconds
    //      after pick (filesystem still settling, OneDrive/iCloud streaming the
    //      blob, antivirus scan). The first retry rescues these without
    //      bothering the user.
    //   3) Telemetry: every attempt emits a `filebytes_prime_attempt` event
    //      via window.__ae (when available) so /pino can see retry success
    //      vs failure rate by error class. The legacy sendBeacon stays for
    //      pre-consent users who declined cookies.
    // Strategy ladder — each attempt reads DIFFERENTLY so a retry is not just a
    // slower repeat of the same failing operation (the pre-2026-07-05 bug):
    //   0) whole-file file.arrayBuffer()  — fast path, succeeds ~99 % of the time
    //   1) chunked 8 MB slices            — rescues transient large-file NotReadable
    //   2) chunked 4 MB slices            — last resort, smallest independent reads
    // A rung is only a different attempt if it issues a DIFFERENT read.
    // `readChunked(file, N)` on a file of `size <= N` slices exactly once and
    // calls .arrayBuffer() on that one slice - i.e. it is the whole-file read
    // that just failed, spelled differently. Measured on the RU shard over 30 d
    // to 26.08: 849 of 2738 fatal NotReadableError primes were on files that
    // round to <=1 MB and 1572 more at 2-7 MB, so for 57% of the population the
    // "escalating strategy ladder" was three identical reads and the only real
    // variable was the 200/800 ms wait between them. Say which it is, so the
    // next person measuring `strategy` is measuring something true.
    function strategyFor(attempt){
        if (attempt <= 0) return { kind: 'whole', chunk: 0 };
        var chunk = attempt === 1 ? 8 * 1024 * 1024 : 4 * 1024 * 1024;
        // Needs at least two slices to be a genuinely different read shape.
        if ((file.size || 0) > chunk) return { kind: 'chunked', chunk: chunk };
        return { kind: 'delay', chunk: 0 };
    }
    function readByStrategy(attempt){
        var st = strategyFor(attempt);
        if (st.kind === 'chunked') return readChunked(file, st.chunk);
        return fileToBytes(file, attempt > 0 ? READ_RETRY_FLOOR_MS : READ_FLOOR_MS);
    }
    function doRead(attempt){
        var t0 = Date.now();
        return readByStrategy(attempt)
            .then(function(bytes){
                _map.set(key, { bytes: bytes, primedAt: Date.now() });
                touch(key);
                evictIfOverBudget();
                _noteReadOk(key);
                if (attempt > 0 && typeof window.__ae === 'function') {
                    try { window.__ae('feature_use', { action: 'filebytes_prime_rescued', attempt: attempt, strategy: strategyFor(attempt).kind, ms: Date.now() - t0, fileMB: Math.round((file.size||0)/1048576) }); } catch (_) {}
                }
                return bytes;
            })
            .catch(function(err){
                var errName = (err && err.name) || 'Error';
                var errMsg = (err && err.message) || String(err);
                // OOM / abort → chunking can't help (still allocates full buffer).
                // Fail fast to FILE_LOST instead of burning the chunked attempts.
                if (_readErrorIsFatal(err)) {
                    var fatalKind = _classifyReadError(err);   // 'oom' | 'aborted' — neither is the file's fault
                    if (typeof window.__ae === 'function') {
                        try { window.__ae('feature_use', { action: 'filebytes_prime_failed', errName: errName, kind: fatalKind, attempts: attempt + 1, fatal: 1, fileMB: Math.round((file.size||0)/1048576) }); } catch (_) {}
                    }
                    throw _readFailure(err, fatalKind, file);
                }
                // A read the browser never finished ends the ladder: the next
                // rung waits on the same stuck machinery (chat 714).
                var stalled = !!(err && err.readStalled);
                // Visibility-aware retry path — preserve original behaviour, but
                // escalate straight to the chunked strategy on the post-visible read.
                if (!stalled && attempt === 0 && document && document.hidden) {
                    return whenVisible().then(function(){ return doRead(1); });
                }
                // Escalating retry — up to 2 more attempts, each a DIFFERENT read
                // strategy (8 MB then 4 MB chunked), with backoff.
                if (!stalled && attempt < 2) {
                    var backoff = attempt === 0 ? 200 : 800;
                    if (typeof window.__ae === 'function') {
                        try { window.__ae('feature_use', { action: 'filebytes_prime_retry', attempt: attempt + 1, strategy: strategyFor(attempt + 1).kind, errName: errName, fileMB: Math.round((file.size||0)/1048576) }); } catch (_) {}
                    }
                    return new Promise(function(r){ setTimeout(r, backoff); }).then(function(){ return doRead(attempt + 1); });
                }
                var verdict = _pickVerdict(err, key);
                _dead.set(key, verdict.kind);
                console.warn('[file-buffer-cache] prime failed after retries (flagged dead):', errMsg);
                try {
                    navigator.sendBeacon('/api/log-error', JSON.stringify([{
                        type: 'filebytes-prime-fail',
                        src: 'key=' + key.substring(0, 60) + '|err=' + errMsg.substring(0, 120) + '|name=' + errName + '|attempts=' + (attempt + 1) + '|kind=' + verdict.kind + (verdict.why ? '|why=' + verdict.why : ''),
                        page: location.pathname,
                        ua: navigator.userAgent.substring(0, 200)
                    }]));
                } catch (_) {}
                if (typeof window.__ae === 'function') {
                    try { window.__ae('feature_use', { action: 'filebytes_prime_failed', errName: errName, kind: verdict.kind, why: verdict.why || undefined, stall_s: stalled ? Math.round((err.stallMs || 0) / 1000) : undefined, attempts: attempt + 1, fileMB: Math.round((file.size||0)/1048576) }); } catch (_) {}
                }
                var lost = _fileLostError(errMsg, verdict.kind, errName);
                try { if (verdict.why) lost.browserWhy = verdict.why; } catch (_) {}
                _announceDeath(key, lost);
                throw lost;
            });
    }
    var run = doRead(0);
    _inflight.set(key, run);
    var forget = function () { _inflight.delete(key); };
    run.then(forget, forget);
    return run;
}

/* ── The ONE door for «give me this file» ─────────────────────────────────
   RAM tier → (wait for tier work still in flight) → OPFS tier → live read.

   ⚠️ prime() is «make this file durable», NOT «give me its bytes». Since
   2026-09-03 it answers null for a file above the RAM cap (it spilled the
   file to disk instead), and that null is a correct answer for the uploader's
   fire-and-forget call. It was a catastrophic answer for the two doors that
   used to be built on top of it: asBlob() did `new Blob([null])`, which is
   the FOUR-BYTE TEXT «null», and asFile() did the same in its fall-through.
   FFmpeg then printed «Format detected only with low score of 1 | moov atom
   not found», and /video-merge told people to remove clip N — on the tool's
   OWN output (Nikolai, 08.09.2026, 66–109 MB on a 4 GB machine; Igor, 07.09,
   146 MB on 16 GB: the 100 MB cap is not a low-memory rule). RU shard: 81 of
   the 85 «clip N» sessions after 03.09 carried filebytes_prime_capped, 0 of
   13 before. /video-compressor lost 34 runs the same way through asFile().

   So neither door reads prime()'s value any more. Above the cap the answer
   is the disk-backed File (lazy slices — WORKERFS streams it, zero heap);
   only when NO tier exists — no OPFS at all, or a spill that failed — do we
   read the bytes ourselves, and we say so in telemetry, because that heap
   copy is exactly what the cap exists to avoid.

   `opts.noRead` — return null instead of that live read (settle() promises
   never to start a read). Guard: tests/js/filebytes-durable-tier-contract. */
async function _durableFile(file, name, mime, opts){
    if (!file) throw new Error('FileBytes: no file');
    var key = keyFor(file);
    var outName = name || (file && file.name) || 'input.bin';
    var outType = mime || (file && file.type) || 'application/octet-stream';
    /* The copy carries the pick's lastModified (2026-10-30). Without it every
       durable File was stamped "now", and name|size|lastModified — the identity
       keyFor() itself, the daily-cap file key (__toolLimit.fileKey) and
       AudioRecover's memo all use — changed under the tool 38 ms into the pick:
       a repeat of today's file was billed again, the memo missed (85 sessions
       on /repair-audio ran the same failing recovery twice or more in 60 days),
       and /repair-video had to stamp an `originalLimitKey` by hand to survive
       it. Same bytes, same identity. */
    var outOpts = { type: outType };
    if (file && file.lastModified) outOpts.lastModified = file.lastModified;
    var cached = key && _map.get(key);
    if (cached && cached.bytes && cached.bytes.length > 0) {
        touch(key);
        return new File([cached.bytes], outName, outOpts);
    }
    /* A tier may still be under construction (pick-time prime or spill in
       flight): join it instead of reading the same file a second time. This
       is the race /video-compressor hit — asFile() consulted the spill map
       while the spill was still streaming and fell through to the null. */
    var wait = key && (_inflight.get(key) || _spillInflight.get(key));
    if (wait) { try { await wait; } catch(_){} }
    cached = key && _map.get(key);
    if (cached && cached.bytes && cached.bytes.length > 0) {
        touch(key);
        return new File([cached.bytes], outName, outOpts);
    }
    /* OPFS disk tier — handle.getFile() gives a disk-backed File whose
       slices are read lazily (WORKERFS-friendly, zero heap). Wrap in a
       new File to restore the original name (extension drives FFmpeg
       format detection) — Blob composition is lazy, no byte copy. */
    var sp = key && _spillMap.get(key);
    if (sp) {
        var dir = await _opfsDir();
        if (dir) {
            try {
                var fh = await dir.getFileHandle(sp.name);
                var f = await fh.getFile();
                if (f && f.size === sp.size) return new File([f], outName, outOpts);
            } catch(_){ /* fall through to live read */ }
        }
    }
    if (opts && opts.noRead) return null;
    /* Neither tier — read the bytes now (chunk-rescued, FILE_LOST-typed).
       Above the cap this is the heap copy the cap was written to avoid, so
       it is named: how often the disk tier is missing is the one number that
       decides whether this branch may stay. */
    if (_tooBigForRam(file) && typeof window !== 'undefined' && typeof window.__ae === 'function') {
        try { window.__ae('feature_use', { f: 'filebytes_capped_live_read', szMB: Math.round((file.size || 0) / 1048576), had_spill: !!sp }); } catch(_){}
    }
    var bytes = await readBytes(file);
    return new File([bytes], outName, outOpts);
}

/* → Blob (a File IS a Blob). Above the cap this is the disk-backed File —
   callers that only hand it to FFmpeg / a <video> / a Worker pay no heap. */
async function asBlob(file, mime){
    return _durableFile(file, file && file.name, mime);
}

/* → File with the original name (or `name`), same tiers as asBlob. */
async function asFile(file, name){
    return _durableFile(file, name);
}

/* Durable handle WITHOUT starting new work.
 *
 * The uploader decides at pick time which tier a file gets (RAM prime ≤ cap,
 * OPFS spill above it, nothing for read-once zones) and kicks it off
 * fire-and-forget. A tool that then hands the RAW picker File to a `<video>`
 * element is reading the same OS handle in parallel with that copy — and on
 * iOS the Photos-picker file is a TEMPORARY transcode whose handle the OS can
 * revoke while the media element is still streaming it. Field shape on
 * /remove-text-from-video (60 days, iOS): .mov picks (= Photos picker) reach
 * `rt_file_loaded` 72% under 10 MB but ~50% above, while .mp4 picks (= Files
 * app) hold ~80% at every size.
 *
 * settle() waits for whatever tier the uploader already started, then returns
 * the durable File for that tier (RAM-backed or OPFS-backed, lazily sliced).
 * When no tier was started it returns the live file untouched — it NEVER
 * triggers a whole-file live read, so it is safe to await for a 4 GB pick.
 * Rejections of the tier work also fall back to the live file: a failed copy
 * must not turn into a failed load. */
/* Where a picked file's handle can die on its own: the phone and tablet OSes
   (iOS/iPadOS Photos-picker transcodes, Android content providers). */
function _handleCanDieOnItsOwn(){
    try {
        var ua = navigator.userAgent || '';
        if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
        if (/Macintosh/i.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document) return true;   // iPadOS
        if (navigator.userAgentData && navigator.userAgentData.mobile) return true;
    } catch (_) {}
    return false;
}
var _settleSeen = new Set();
function _reportSettle(file, key, data){
    if (_settleSeen.has(key)) return;
    _settleSeen.add(key);
    if (typeof window.__ae !== 'function') return;
    try { window.__ae('feature_use', Object.assign({ f: 'filebytes_settle', szMB: Math.round(((file && file.size) || 0) / 1048576) }, data)); } catch (_) {}
}

async function settle(file){
    var key = keyFor(file);
    if (!key) return file;
    var prime = _inflight.get(key), copy = _spillInflight.get(key);
    var wait = prime || copy;
    /* A copy still being written is NOT waited for on a desktop (2026-09-27).
       On a phone the live handle dies on its own within minutes and the copy
       is what the work runs on. On a desktop it dies only when the file is
       changed, moved or deleted behind the page — measured RU, 13–27.09, files
       over 100 MB: the copy rescued 38 of 13 001 desktop sessions (26 still
       delivered), while desktop sessions WITHOUT a copy lost the file in 1 of
       1 994. Waiting for the copy, meanwhile, was paid by every one of those
       sessions whose tool settles first (/mute-video's whole remux is a
       second; the copy is 4.4 s a GB on a fast NVMe). So a desktop tool starts
       on the live file at once; a copy that is already FINISHED is still
       handed back, and the copy in flight keeps completing as the rescue for
       the paths that meet a dead handle later (FFmpeg mounts, the WebCodecs
       reader, read()). */
    if (wait && copy && !prime && !_handleCanDieOnItsOwn()) {
        _reportSettle(file, key, { waited_ms: 0, skipped: 1 });
        return file;
    }
    if (wait) {
        var t0 = Date.now();
        try { await wait; } catch(_){ return file; }
        if (copy) _reportSettle(file, key, { waited_ms: Date.now() - t0, skipped: 0 });
    }
    if (!_map.has(key) && !_spillMap.has(key)) return file;
    /* noRead: a spill whose disk copy cannot be opened must hand back the live
       file, not start the whole-file read this door promises never to start. */
    try { return carryGate(file, (await _durableFile(file, file && file.name, null, { noRead: true })) || file); } catch(_){ return file; }
}

/* The uploader zone stamps the files it accepted (file.__tuGate — see
   stampAccepted in tool-uploader.js); the durable File this module hands back
   is a different object holding the same bytes, so the verdict rides along.
   Without this a tool that validates the SETTLED file would fall back to its
   own kind — the second-owner class the stamp exists to close. */
function carryGate(src, out){
    if (out && out !== src && src && src.__tuGate && !out.__tuGate) {
        try { Object.defineProperty(out, '__tuGate', { value: src.__tuGate, configurable: true }); } catch(_){}
    }
    return out;
}

/* Non-reading peek: are this file's bytes already in the cache? Unlike
   get()/read(), never triggers a prime — safe to call from hot paths and
   for multi-hundred-MB files where an accidental prime would blow the
   heap. Used by the WORKERFS mount rescue in ffmpeg-processor: when the
   OS file handle has died but the bytes were primed earlier (pick-time
   prime or decode-time prime), the mount swaps in a RAM-backed File
   instead of failing with FILE_LOST. */
function hasCached(file){
    var key = keyFor(file);
    return !!(key && _map.has(key));
}

/* ── OPFS disk tier ("spill") — survivability for files ABOVE the RAM
      prime cap ─────────────────────────────────────────────────────────
   The RAM cache deliberately skips huge files (uploader gates at 100 MB —
   pulling 700 MB into the JS heap at pick time defeats the point of
   WORKERFS streaming). But that left the >100 MB band with ZERO handle-
   death protection: telemetry 2026-07-03 shows 104 of 407 FILE_LOST
   events / 14 d (26%) carried file_size > 100 MB — every one an export
   lost AFTER the user arranged their work.

   OPFS (navigator.storage.getDirectory) is the right disk for this:
   - `file.stream().pipeTo(createWritable())` copies pick→disk with no
     RAM spike (chunked pipe, browser-managed backpressure).
   - `handle.getFile()` returns a real disk-backed File — WORKERFS
     mounts it directly and reads slices lazily; zero heap cost.
   - Same-origin persistent quota (typically ≥ 60% of free disk).
   Prior art in this codebase: canvas-video-export + video-merge R6.2
   already use OPFS for large OUTPUTS; this extends the pattern to
   INPUTS behind the same FileBytes façade (single source of truth for
   file survivability — enhance-video Wave 22 lesson). */
var _spillMap = new Map();   // key → { name, size, spilledAt }
var _spillInflight = new Map(); // key → Promise (dedupe concurrent spill() calls)
var _spillChain = Promise.resolve(); // serializes writers — see spill()
var _spillSeq = 0;
var _opfsDirP = null;
var SPILL_DIR = 'fb-spill';

/* Where the copies live (2026-09-27): a storage bucket of their own, never
   persisted, where the browser has buckets (Chromium 122+).
   The copy is the most disposable thing the site keeps — the person's file is
   still on their disk — and until now it shared the site's default bucket with
   everything else. That bucket is exactly what Chromium protects once the site
   holds the durable-storage permission (the permission covers the whole origin;
   measured in real Chrome, tests/playwright/work-survives-disk-pressure.mjs R2),
   and saved work now asks for that permission (TranscriptCheckpointStore's
   `work` bucket): a copy orphaned by a crash would then stay on the person's
   disk until they came back to the site, and forever if they never did.
   In its own non-persistent bucket it stays reclaimable by the browser whatever
   the site is granted, and it is evicted as its own unit instead of taking the
   model caches and drafts of the default bucket with it. 'relaxed' durability:
   a copy lost to a power cut costs nothing, the file is on the disk.
   Elsewhere (Firefox, WebKit) the default origin root, as before. The OPFS
   janitor (timbrica-core __opfsOwner) sweeps both places. */
var COPY_BUCKET = 'copies';
var _copyInBucket = false;
function _opfsDir(){
    if (_opfsDirP) return _opfsDirP;
    _opfsDirP = (async function(){
        try {
            if (!navigator.storage || typeof navigator.storage.getDirectory !== 'function') return null;
            var sb = navigator.storageBuckets;
            if (sb && typeof sb.open === 'function') {
                try {
                    var b = await sb.open(COPY_BUCKET, { persisted: false, durability: 'relaxed' });
                    if (b && typeof b.getDirectory === 'function') {
                        var bd = await (await b.getDirectory()).getDirectoryHandle(SPILL_DIR, { create: true });
                        _copyInBucket = true;
                        return bd;
                    }
                } catch (_) { /* fall back to the origin root */ }
            }
            var root = await navigator.storage.getDirectory();
            return await root.getDirectoryHandle(SPILL_DIR, { create: true });
        } catch (_) { return null; }
    })();
    return _opfsDirP;
}
/* Copies written before 27.09 — and a restore manifest may still name one —
   live in the origin root's fb-spill. Read-only lookup; never created. */
async function _legacySpillDir(){
    try {
        if (!_copyInBucket) return null;
        var root = await navigator.storage.getDirectory();
        return await root.getDirectoryHandle(SPILL_DIR);
    } catch (_) { return null; }
}

/* Copy the file's bytes to OPFS while the handle is still alive.
   Idempotent per cache key. Resolves true when spilled, null when OPFS
   is unavailable (private browsing, old Safari) or the copy failed —
   callers treat null as "no disk tier, handle death stays fatal".

   Writers are SERIALIZED through one queue: a multi-file pick used to
   stream every file to OPFS concurrently, and on iOS the SQLite-backed
   OPFS drops its connection under exactly that pressure (the
   TIMBRICA-FRONTEND-AY "Connection is lost" class; on 02.08 a 634 MB
   three-clip pick lost its whole tab mid-spill on an iPhone). One
   writer at a time halves the peak IO/memory churn and lets each copy
   finish while its picker handle is still alive. Concurrent calls for
   the SAME file join the in-flight promise instead of queueing twice. */
function spill(file){
    if (!(file instanceof File) && !(file instanceof Blob)) return Promise.resolve(null);
    var key = keyFor(file);
    if (!key) return Promise.resolve(null);
    if (_spillMap.has(key)) return Promise.resolve(true);
    var joined = _spillInflight.get(key);
    if (joined) return joined;
    var run = _spillChain.then(function(){ return _doSpill(file, key); });
    _spillChain = run.then(function(){}, function(){});
    _spillInflight.set(key, run);
    var forget = function(){ _spillInflight.delete(key); };
    run.then(forget, forget);
    return run;
}

/* Will this file plausibly fit in what the origin is still allowed to store?
   Measured 2026-08-10 in Chromium: quota came back as 4 GB, so a 13.2 GB pick
   streamed 1.86 GB to disk over 9.4 s and THEN failed on quota — every byte of
   that write wasted, and on a slower disk it is minutes of contention against
   the WORKERFS reads the user is actually waiting for. That is the shape of
   bug-report #1345 on /archive-extractor: pick a 13.5 GB archive, watch nothing
   happen. The spill exists to survive handle death; it is insurance, and
   insurance that cannot pay out is not worth the premium.

   Best-effort by design: when estimate() is unavailable we keep the old
   behaviour (try, and let the copy fail if it must) rather than refusing to
   protect a file we might well have been able to protect. */
var SPILL_HEADROOM = 1.10;   // quota accounting is approximate; leave slack

/* …and the copy must not push the disk into the browser's EVICTION zone
   (2026-09-27, support chat 640). The grant Chromium reports keeps 1 GB of the
   disk free («must remain available»), but its evictor already starts at 2 GB
   («should remain available», min(2 GB, 10 % of the disk);
   storage/browser/quota/quota_settings.cc): a round 5 minutes after the browser
   starts and every 30 minutes after that deletes whole non-persistent buckets,
   least recently used first, until the shortage is covered
   (QuotaDatabase::GetBucketsForEviction — there is no «page is open»
   exception). A copy written into that 1 GB band is insurance the browser
   itself cancels, and it takes our other data with it: on 21.09 six picks
   copied 6 GB to one person's disk and our whole origin went 467 MB → 2 MB;
   on 26.09 a 769 MB copy left ~1.1 GB free, the machine rebooted, and 2 hours
   of her censor edits were gone with the rest of the origin (device_cap.su
   306 → 0 while the login cookie lived on). So the copy needs the gap between
   the two lines on top of its own size — the default gap on any disk over
   100 GB, and a conservative one below that, where both lines shrink. */
var SPILL_EVICTION_GAP = 1024 * 1024 * 1024;

/* Above this the insurance costs more than the work it insures (2026-09-17).
   The spill exists so a lane can settle() onto a copy that survives a revoked
   picker handle — a phone class (iOS Photos picker, Android content
   providers). But settle() WAITS for a spill in flight, so every lane that
   settles first pays the whole disk copy up front: measured on /mute-video,
   whose main lane rewrites the moov and copies nothing — a 3.3 GB pick took
   89 s (the OPFS copy at ~40 MB/s) and the SAME lane on a 10.35 GB pick took
   1.1 s, because that spill had been skipped on quota. And the copy stays on
   the user's disk until the 24 h sweep. Phones are capped by their own
   memory term far below this line (dm=4 → 2 GiB), so the ceiling changes
   nothing there; on a desktop a file this size keeps the live handle, as it
   does when the quota is short — the same fail-open path. */
var SPILL_MAX_BYTES = 2 * 1024 * 1024 * 1024;

async function _spillFits(file){
    try {
        if (!navigator.storage || typeof navigator.storage.estimate !== 'function') return true;
        /* The REAL grant (window.__storageEstimate, timbrica-core.js): on most
           Chromium builds estimate() says usage + 10 GiB whatever is left, so a
           spill was attempted — and refused — on every nearly full disk
           (filebytes_spill_failed QuotaExceededError: 570 sessions / 30 d to
           25.09.2026). Measured up to exactly what this spill needs. */
        var need = (file.size || 0) * SPILL_HEADROOM + SPILL_EVICTION_GAP;
        var est = await window.__storageEstimate({ upTo: need });
        if (!est || !est.quota) return true;
        var free = est.quota - (est.usage || 0);
        return need <= free;
    } catch (_) { return true; }
}

async function _doSpill(file, key){
    if (_spillMap.has(key)) return true;
    if ((file.size || 0) > SPILL_MAX_BYTES) {
        if (typeof window.__ae === 'function') {
            try { window.__ae('feature_use', { f: 'filebytes_spill_skipped', szMB: Math.round((file.size||0)/1048576), reason: 'ceiling' }); } catch(_){}
        }
        return null;
    }
    if (!(await _spillFits(file))) {
        if (typeof window.__ae === 'function') {
            try { window.__ae('feature_use', { f: 'filebytes_spill_skipped', szMB: Math.round((file.size||0)/1048576), reason: 'quota' }); } catch(_){}
        }
        return null;
    }
    /* Name carries the creation timestamp so the startup sweep can
       distinguish a crashed session's orphan (old) from a LIVE spill
       owned by another concurrently-open tab (OPFS is origin-scoped
       and shared across tabs — deleting blindly at load would strip a
       neighbouring tab's disk tier mid-session). */
    /* …and the owner tag (window.__opfsOwner, timbrica-core.js): the next page
       load removes the copy as soon as THIS page is gone, instead of 24 h
       later: on a phone every picked video above the RAM tier is copied, and
       support chat 649's iPad held tens of GB of our files. */
    var name = 's' + Date.now() + '-' + (++_spillSeq) + '-' + (file.size || 0)
        + ((window.__opfsOwner && window.__opfsOwner.tag('-')) || '');
    var dir, stage = 'dir';
    try {
        // _opfsDir() lives INSIDE the try: on iOS 18 Safari the storage layer can
        // reject with "UnknownError: Connection is lost" (OPFS is SQLite-backed
        // there) under memory pressure / backgrounding. spill() is called
        // fire-and-forget by persist(), so a reject here escaped as an
        // onunhandledrejection (TIMBRICA-FRONTEND-AY). Keep it a null-returning
        // best-effort — the caller already falls back to a fresh file read.
        dir = await _opfsDir();
        if (!dir) return null;
        stage = 'handle';
        var fh = await dir.getFileHandle(name, { create: true });
        stage = typeof fh.createWritable === 'function' ? 'writable' : 'no_writable';
        var w = await fh.createWritable();
        stage = 'pipe';
        var tCopy = Date.now();
        await file.stream().pipeTo(w);
        _spillMap.set(key, { name: name, size: file.size || 0, spilledAt: Date.now() });
        /* ms: how long the copy took on this device — the price every
           settle() used to wait for, unmeasured until 27.09.2026 (a 1 GB copy is
           4.4 s on a fast NVMe with the file already cached; spinning disks and
           SATA SSDs are several times that). bkt: in the copies bucket or not. */
        if (typeof window.__ae === 'function') {
            try { window.__ae('feature_use', { f: 'filebytes_spill_ok', szMB: Math.round((file.size||0)/1048576), ms: Date.now() - tCopy, bkt: _copyInBucket ? 1 : 0 }); } catch(_){}
        }
        return true;
    } catch (err) {
        // removeEntry() is ASYNC — its promise rejects when the OPFS connection
        // is lost, and a sync try/catch cannot catch that: the rejection escaped
        // as a fresh onunhandledrejection from this very cleanup line (the
        // TIMBRICA-FRONTEND-AY recurrence). Chain .catch instead.
        if (dir) { try { var _rm = dir.removeEntry(name); if (_rm && _rm.catch) _rm.catch(function(){}); } catch(_){} }
        if (typeof window.__ae === 'function') {
            try { window.__ae('feature_use', { f: 'filebytes_spill_failed', szMB: Math.round((file.size||0)/1048576), errName: (err && err.name) || 'Error',
                stage: stage, msg: String((err && err.message) || '').slice(0, 100) }); } catch(_){}
        }
        return null;
    }
}

/* One call that makes ANY picked file survivable, routing by size:
   ≤ RAM cap → prime into the RAM cache (existing fast path);
   >  RAM cap → spill to OPFS disk (no heap cost).
   Fire-and-forget from pick handlers. */
var PERSIST_RAM_MAX = 100 * 1024 * 1024;
function persist(file){
    if (!(file instanceof File) && !(file instanceof Blob)) return Promise.resolve(null);
    if ((file.size || 0) <= PERSIST_RAM_MAX) return prime(file).catch(function(){ return null; });
    return spill(file);
}

/* Peek across BOTH tiers. */
function hasPersisted(file){
    var key = keyFor(file);
    return !!(key && (_map.has(key) || _spillMap.has(key)));
}

/* Non-reading peek at the OPFS spill tier: {name,size} when this file's bytes
   are on disk, else null. Project-restore flows persist the name in their own
   manifest and hand it back to readSpilled() after a reload — the in-memory
   _spillMap dies with the page, but the OPFS file survives (swept at ~24 h). */
function spillInfo(file){
    var key = keyFor(file);
    var sp = key && _spillMap.get(key);
    /* A name handed out for a manifest is read by a LATER page: pinned for the
       longest manifest (music-video-maker's saved result, 24 h), or the owner
       janitor would remove it the moment this page is gone. */
    if (sp) _pinSpill(sp.name);
    return sp ? { name: sp.name, size: sp.size } : null;
}
var SPILL_PIN_MS = 24 * 60 * 60 * 1000;
function _pinSpill(name){
    try { if (window.__opfsOwner && typeof window.__opfsOwner.pin === 'function') window.__opfsOwner.pin(name, SPILL_PIN_MS); } catch(_){}
}

/* Rehydrate a spilled file AFTER a reload, by the OPFS name a manifest kept.
   Returns a real File (disk-backed, lazy slices — zero heap cost) or null when
   the entry is gone (24 h sweep, private mode, quota eviction). `meta` restores
   the original identity — name/type/lastModified drive keyFor(), format
   sniffing and FFmpeg extension detection downstream — and its `size` is
   verified against the disk copy so a truncated write never resurrects as a
   corrupt "restored" file. Re-registers the entry in _spillMap so handle-death
   rescue covers the restored File immediately. */
async function readSpilled(spillName, meta){
    try {
        if (!spillName) return null;
        var dir = await _opfsDir();
        if (!dir) return null;
        var fh;
        try { fh = await dir.getFileHandle(spillName); }
        catch (notHere) {
            var old = await _legacySpillDir();
            if (!old) throw notHere;
            fh = await old.getFileHandle(spillName);
        }
        var f = await fh.getFile();
        if (!f || !f.size) return null;
        if (meta && meta.size && f.size !== meta.size) return null;
        var out = new File([f], (meta && meta.name) || spillName, {
            type: (meta && meta.type) || '',
            lastModified: (meta && meta.lastModified) || Date.now(),
        });
        var key = keyFor(out);
        if (key && !_spillMap.has(key)) _spillMap.set(key, { name: spillName, size: f.size, spilledAt: Date.now() });
        _pinSpill(spillName);   // its owner is the page that died; this one reads it now
        return out;
    } catch (_) { return null; }
}

/* Startup sweep: crashed/closed sessions leave orphan spills (their
   _spillMap died with the page). OPFS is origin-scoped and SHARED
   across tabs, so we must NOT delete blindly — a neighbouring open tab
   may own fresh spills. The filename embeds the creation timestamp
   ('s<ts>-<seq>-<size>'); delete only entries older than 24 h (a live
   tool session never spans that) plus anything unparseable. */
(function _sweepStaleSpills(){
    var MAX_AGE_MS = 24 * 60 * 60 * 1000;
    var run = function(){
        _opfsDir().then(async function(dir){
            if (!dir) return;
            try {
                var names = [];
                /* ⚠️ Здесь было `for await (var entry of dir.keys())`. Safari
                   отвергает эту форму на разборе — «Unexpected identifier
                   'await'. Expected '(' to start a for-loop header» — и падает
                   НЕ вызов, а весь ФАЙЛ: FileBufferCache тогда не определяется
                   вовсе. А это защита от FILE_LOST (~100 случаев в неделю),
                   подключённая на восьми тулах. Замер 30 дней: 11 событий у
                   Safari, все с этой подписью. Ручной обход итератора читается
                   каждым движком и делает ровно то же. */
                var _it = dir.keys(), _st;
                while (!(_st = await _it.next()).done) names.push(_st.value);
                var now = Date.now();
                for (var i = 0; i < names.length; i++) {
                    // An owner-tagged copy is the owner janitor's (timbrica-core.js):
                    // it goes when its page does, and a live page's stays.
                    if (window.__opfsOwner && window.__opfsOwner.ownerOf(names[i])) continue;
                    var m = names[i].match(/^s(\d+)-/);
                    var ts = m ? parseInt(m[1], 10) : 0;
                    if (!ts || (now - ts) > MAX_AGE_MS) {
                        try { await dir.removeEntry(names[i]); } catch(_){}
                    }
                }
            } catch(_){}
        });
    };
    if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 10000 });
    else setTimeout(run, 3000);
})();

function release(file){
    var key = keyFor(file);
    if (!key) return;
    _map.delete(key);
    _dead.delete(key);  // user picked again — give the new handle a fresh try
    var idx = _order.indexOf(key); if (idx !== -1) _order.splice(idx, 1);
    var sp = _spillMap.get(key);
    if (sp) {
        _spillMap.delete(key);
        _opfsDir().then(function(dir){ if (dir) { try { dir.removeEntry(sp.name); } catch(_){} } });
    }
}

/* releaseRam(file) — drop ONLY the RAM tier, KEEP the OPFS spill.
   For "the heavy in-memory bytes are no longer needed RIGHT NOW, but the file
   may be read again later" — e.g. a tool that has finished producing its output
   and is now just showing/downloading it, while the inputs sit primed in RAM for
   nothing. Unlike release(), the disk-backed spill survives, so a later read
   (asFile → OPFS tier, or readBytes' spill consult) still recovers the bytes with
   zero heap and no FILE_LOST — the survivability guard stays intact. Frees the
   pressure that was evicting large output blobs on the Windows/Yandex cohort
   (video-merge «загрузка прервана» / preview code:4). */
function releaseRam(file){
    var key = keyFor(file);
    if (!key) return;
    _map.delete(key);
    var idx = _order.indexOf(key); if (idx !== -1) _order.splice(idx, 1);
    // _dead and _spillMap intentionally kept: a later read must still find the
    // disk tier (and not retry a known-dead handle before consulting it).
}

function clear(){
    _map.clear();
    _dead.clear();
    _order.length = 0;
    _spillMap.forEach(function(sp){
        _opfsDir().then(function(dir){ if (dir) { try { dir.removeEntry(sp.name); } catch(_){} } });
    });
    _spillMap.clear();
}

function stats(){
    var total = 0;
    _map.forEach(function(v){ total += v.bytes.length; });
    return { count: _map.size, totalBytes: total };
}

// FileBytes — explicit façade that the rest of the codebase uses instead of
// File.prototype.arrayBuffer. Tools opt IN by calling FileBytes.read(file), which:
//   1. Returns the cached Uint8Array if we've seen this file before
//   2. Otherwise reads it now, caches the bytes, returns them
//   3. If the underlying File handle has gone stale (NotFoundError), tries the cache
//      one last time before surfacing the error
// This replaces the earlier File.prototype monkey-patch — explicit API is safer for
// debugging (breakpoints land where you expect), won't conflict with third-party
// patches, and the cache is observable (FileBufferCache.stats()).
async function readBytes(file){
    if (!file) throw new Error('FileBytes.read: no file');
    var key = keyFor(file);
    var cached = key && _map.get(key);
    if (cached && cached.bytes && cached.bytes.length > 0) {
        touch(key);
        // Return a fresh Uint8Array (with its own ArrayBuffer) so that the
        // moment a consumer hands the bytes to a Worker via postMessage's
        // transfer list, our cache copy stays alive for the next consumer.
        // Without this slice, two callers on the same file shared one
        // buffer — the first to do ffmpeg.writeFile() detached it for
        // everyone else and the next read returned a 0-length view, which
        // surfaced in production as "ArrayBuffer at index 0 is already
        // detached" (16 sessions in 30 d, audio-to-video alone). The
        // analogous fix already exists for readArrayBuffer() below.
        return cached.bytes.slice();
    }
    // OPFS spill tier — bytes for files ABOVE the RAM prime cap live on disk
    // (uploader spills >100 MB at pick). asFile() consulted this tier since
    // 2026-07-03; readBytes NEVER did, so every process()-time read of a big
    // file whose picker handle had died threw FILE_LOST while byte-identical
    // data sat in OPFS. Measured cost 2026-07-21: mute-video alone — 76
    // wasm_process FILE_LOST / 14 d, 36 of them 50–200 MB files, all with a
    // SUCCESSFUL select-time prime in session. Consult the spill BEFORE the
    // live read: it is exact bytes on OUR disk, strictly more reliable than a
    // picker handle minutes after pick.
    var _spillBytes = async function () {
        var sp = key && _spillMap.get(key);
        if (!sp) return null;
        try {
            var dir = await _opfsDir();
            if (!dir) return null;
            var fh = await dir.getFileHandle(sp.name);
            var f = await fh.getFile();
            if (!f || f.size !== sp.size) return null;
            var b = new Uint8Array(await f.arrayBuffer());
            try { if (typeof window.__ae === 'function') window.__ae('feature_use', { action: 'filebytes_spill_hit', fileMB: Math.round((sp.size || 0) / 1048576) }); } catch (_) {}
            return b;
        } catch (_) { return null; }
    };
    var spilled = await _spillBytes();
    if (spilled && spilled.length > 0) return spilled;

    // Fast-fail on files we already know are unreadable. Previously this
    // code path retried arrayBuffer() on every read, so one revoked handle
    // surfaced 10+ NotReadableError events per enhance-video session.
    if (key && _dead.has(key)) throw _fileLostError('previously failed', _dead.get(key));

    try {
        var bytes2;
        try {
            bytes2 = await fileToBytes(file);               // fast whole-file read
        } catch (whole) {
            // Direct read (no prime ran, or cache miss) — give the chunked
            // strategy one shot before declaring the handle dead. Skip only on
            // OOM/abort where chunking can't help. Mirrors prime()'s escalation
            // so a file read directly (not via the uploader prime) is just as
            // resilient to transient large-file NotReadableError.
            if (_readErrorIsFatal(whole)) throw whole;
            bytes2 = await readChunked(file, 8 * 1024 * 1024);
            if (typeof window.__ae === 'function') {
                try { window.__ae('feature_use', { action: 'filebytes_read_rescued', strategy: 'chunked', errName: (whole && whole.name) || 'Error', fileMB: Math.round((file.size||0)/1048576) }); } catch (_) {}
            }
        }
        var ab = bytes2.buffer;
        /* The RAM tier obeys ONE rule, on every write into it: nothing above the
           cap (_tooBigForRam). Until 2026-09-09 that rule lived in prime() only,
           and a read() miss cached whatever it had just read — so a 70 MB video
           on a 4 GB phone, refused by prime(), landed in the heap anyway through
           the other door, twice (cache + the caller's copy). Above the cap the
           bytes are handed over UNCOPIED and uncached: the caller owns them, and
           the OPFS spill (filled at pick) keeps the handle-death insurance. */
        if (key && ab && ab.byteLength > 0 && !_tooBigForRam(file)) {
            var bytes = new Uint8Array(ab);
            _map.set(key, { bytes: bytes, primedAt: Date.now() });
            touch(key);
            evictIfOverBudget();
            // Same reasoning as the cache-hit branch — give callers a copy
            // they own so the cache survives a transfer.
            return bytes.slice();
        }
        return new Uint8Array(ab || 0);
    } catch (err) {
        // Handle rotted — maybe a previous read cached the bytes (return
        // a fresh copy for the same transfer-safety reason as above).
        if (cached && cached.bytes && cached.bytes.length > 0) { touch(key); return cached.bytes.slice(); }
        // Nothing cached, handle dead — flag for subsequent reads and throw
        // a consistent FILE_LOST error shape that downstream classifyError()
        // helpers already know how to route.
        // Diagnostic breadcrumb: WHICH tiers existed at death. The mute-video
        // <50 MB process-time cohort (2026-07-21) died with a successful
        // select-prime in session and we could not tell from telemetry whether
        // the RAM entry was evicted, detached, or never keyed — this answers it.
        try {
            if (typeof window.__ae === 'function') window.__ae('feature_use', {
                action: 'filebytes_read_dead',
                had_ram: !!(cached && cached.bytes && cached.bytes.length > 0),
                had_spill: !!(key && _spillMap.has(key)),
                ram_entries: _map.size, ram_mb: Math.round(_order.reduce(function (s, k) { var e = _map.get(k); return s + ((e && e.bytes && e.bytes.length) || 0); }, 0) / 1048576),
                fileMB: Math.round(((file && file.size) || 0) / 1048576),
                errName: (err && err.name) || 'Error'
            });
        } catch (_) {}
        var _k = _classifyReadError(err);
        // only a lost file is dead; a memory wall or a cancel says nothing about it
        if (key && _k !== 'oom' && _k !== 'aborted') _dead.set(key, _k);
        var _lost = _readFailure(err, _k, file);
        _announceDeath(key, _lost);
        throw _lost;
    }
}

/* whenUnreadable(file) — settles ONLY when this file has been judged unreadable:
   rejects with the typed FILE_LOST error the uploader shows. Never resolves —
   race it against your own work (AudioDecoderRobust.robust does) and call
   `.release()` when your work is over.

   Why a door and not deadlines everywhere: a decoder reads the file in a dozen
   places of its own (headers, probes, demux windows, FFmpeg's mount), and in
   chat 714 it hung in one of them — headerAudioInfo's 512 KB slice — while the
   uploader's read hung in another. Giving each of those its own deadline is a
   hunt that never ends; the pick-time read already has the bound and the
   evidence, so everyone waiting on the file ends with it. No read is started
   here: the waiter hears the next verdict of a read someone else runs (the
   uploader's pick-time read, or the decoder's own). A memory wall and a cancel
   are not a dead file and do not settle it. */
var _deathWaiters = new Map();   // key → [reject, …] — see whenUnreadable()
function whenUnreadable(file){
    var key = keyFor(file), rej = null;
    var p = new Promise(function(_, reject){ rej = reject; });
    // A waiter that has its answer some other way lets go — the list is global,
    // and a forgotten entry would keep the waiter's whole result alive.
    p.release = function(){
        var w = key && _deathWaiters.get(key);
        if (!w) return;
        var i = w.indexOf(rej);
        if (i >= 0) w.splice(i, 1);
        if (!w.length) _deathWaiters.delete(key);
    };
    if (!key) return p;
    // Joins the NEXT verdict — of the read in flight, or of the next one: a file
    // judged dead earlier gets a fresh read from whoever asks for its bytes
    // (prime() clears the mark), and only that read's verdict speaks for it.
    var w = _deathWaiters.get(key) || [];
    w.push(rej);
    _deathWaiters.set(key, w);
    return p;
}
/* Called where a read ladder ends in a dead file (prime, the spill probe,
   readBytes). After a macrotask: the uploader's own handler on the same read
   says the verdict first and marks the error (__userNotified), so a waiter's
   catch does not say it a second time. A memory wall or a cancel is not a dead
   file and wakes nobody. */
function _announceDeath(key, err){
    var k = err && err.fileLostKind;
    if (!key || !k || k === 'oom' || k === 'aborted') return;
    var w = _deathWaiters.get(key);
    if (!w) return;
    _deathWaiters.delete(key);
    setTimeout(function(){
        for (var i = 0; i < w.length; i++) { try { w[i](err); } catch (_) {} }
    }, 0);
}

async function readArrayBuffer(file){
    var bytes = await readBytes(file);
    // Return a fresh ArrayBuffer slice so callers that transfer it don't detach our cache.
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}

window.FileBytes = {
    read: readBytes,           // → Uint8Array (recommended for new code)
    arrayBuffer: readArrayBuffer, // → ArrayBuffer (fresh copy, safe to transfer)
    asBlob: asBlob,             // → Blob (RAM tier → OPFS tier → live read; above the cap it is the disk-backed File, zero heap)
    asFile: asFile,             // → File, same tiers; never null, never the text «null»
    settle: settle,             // → File: durable tier the uploader already made, or the live file; never starts a read
    hasCached: hasCached,       // → boolean peek (RAM tier), never primes
    hasPersisted: hasPersisted, // → boolean peek (RAM + OPFS tiers)
    persist: persist,           // size-routed survivability: ≤100MB prime, else OPFS spill
    spill: spill,               // explicit OPFS spill (streamed, serialized, no heap cost)
    spillInfo: spillInfo,       // → {name,size}|null — OPFS name for restore manifests
    readSpilled: readSpilled,   // → File|null — rehydrate a spill AFTER reload by name
    prime: prime,               // fire-and-forget pre-read on upload
    whenUnreadable: whenUnreadable, // → Promise that rejects (typed FILE_LOST) once the pick-time read judged the file dead; race your own reads against it
    release: release,
    releaseRam: releaseRam,     // drop RAM tier, keep OPFS spill (post-output memory relief)
    clear: clear,
    stats: stats,
    ramBudget: ramBudget,       // aggregate device-aware RAM ceiling for cached bytes
    ramUsed: ramUsed,           // bytes currently held in the RAM tier
    // Exposed for tests + advanced callers that want the raw chunked reader.
    readChunked: readChunked,
    version: '1.1.0'
};

// Legacy alias
window.FileBufferCache = window.FileBytes;

// NOTE: we deliberately do NOT patch File.prototype.arrayBuffer. All user-file reads
// across the codebase go through FileBytes.read() / .arrayBuffer(), which gives us
// explicit control of caching behaviour without surprising the debugger, conflicting
// with other libraries that may patch the same prototype, or making the semantics
// of a native platform API tool-specific.

})();
